import { compareGateNames } from "./gateSort";

/**
 * Per-airport ordering for the retail store selector.
 *
 * Most airports have no opinion about store order and use the generic
 * alphanumeric sort in `gateSort`. An airport that supplies its own order gets
 * a comparator here and an entry in `STORE_COMPARATORS` at the bottom of the
 * file; nothing else in the app needs to change.
 */

/**
 * DFW.
 *
 * DFW supplied an explicit display order for their retail stores rather than a
 * rule we could derive, so the list below is kept verbatim and in sequence — it
 * can be diffed directly against their source list when they revise it.
 *
 * Store names arrive as "<zone>-<store>", e.g. "A21-A25-Starbucks" or
 * "D5-D22 South Village-Lego". Sorting happens in two stages so the list stays
 * maintainable:
 *
 *   1. Zone order is DERIVED (terminal letter, then leading gate number). The
 *      supplied order is exactly that across all 26 zones, so a zone added or
 *      renamed later still lands correctly with no code change.
 *   2. Order WITHIN a zone is curated and cannot be derived, so it comes from
 *      the list. A store that isn't listed sorts after the curated entries of
 *      its own zone rather than falling off the end of the whole dropdown.
 */
const DFW_STORE_ORDER: readonly string[] = [
  "A4-A13-Plum Market Bar",
  "A4-A13-Vending Machine",
  "A4-A13-Einstein Bros. Bagels",
  "A4-A13-Sound Balance",
  "A4-A13-Bonton Farms",
  "A4-A13-TGI Fridays",
  "A4-A13-Whisk & Bowl",
  "A4-A13-Farmer's Fridge Vending",
  "A15-A20-Stockyards Marketplace",
  "A15-A20-Popeyes Louisiana Kitchen",
  "A15-A20-McDonald's",
  "A15-A20-Brighton",
  "A15-A20-Bugatti Bar & Taverna",
  "A15-A20-iStore",
  "A15-A20-Smoothie King",
  "A15-A20-L'Occitane",
  "A15-A20-Travel @ Ease",
  "A15-A20-Vending Machine",
  "A21-A25-Starbucks",
  "A21-A25-Auntie Anne's",
  "A21-A25-Dallas Cowboys Club",
  "A21-A25-3Sixty Duty Free and More",
  "A21-A25-Pappadeaux Seafood Kitchen",
  "A21-A25-XpresSpa",
  "A21-A25-La Madeleine",
  "A21-A25-Brookstone",
  "A21-A25-7-Eleven",
  "A21-A25-Twisted Root",
  "A21-A25-Talie",
  "A21-A25-Smoothie King",
  "A21-A25-Vending Machine",
  "A28-A34-Pappasito's Cantina",
  "A28-A34-Natalie's Candy Jar",
  "A28-A34-Sunglass Hut",
  "A28-A34-Ertekin Fashion Boutique",
  "A28-A34-California Pizza Kitchen",
  "A28-A34-Toys R Us",
  "A28-A34-ICE International Currency Exchange",
  "A28-A34-Kiehl's",
  "A28-A34-Dallas Cowboys Pro Shop",
  "A28-A34-Johnston & Murphy",
  "A28-A34-Lorena Garcia Tapas y Cocina",
  "A28-A34-Vending Machine",
  "A35-A39-Panera Bread",
  "A35-A39-McDonald's",
  "A35-A39-Southwest News",
  "A35-A39-La Crème Coffee & Tea",
  "A35-A39-Starbucks",
  "A35-A39-Minute Suites",
  "A35-A39-Southwest News",
  "A35-A39-Vending Machine",
  "B1-B12-Plum Market",
  "B1-B12-Dunkin'",
  "B1-B12-Texas Marketplace",
  "B1-B12-TGI Fridays",
  "B1-B12-Néktər Juice Bar",
  "B1-B12-Cousin's Bar-B-Q",
  "B1-B12-Smashburger",
  "B1-B12-Texas Monthly News",
  "B1-B12-Vending Machine",
  "B15-B24-Panda Express",
  "B15-B24-Raising Cane's Chicken Fingers",
  "B15-B24-Texas Life Bar",
  "B15-B24-TGI Fridays On the Fly",
  "B15-B24-CultureMap",
  "B15-B24-Portillo's",
  "B15-B24-Vending Machine",
  "B25-B28-Hickory by Kent Rathbun",
  "B25-B28-Be Relax",
  "B25-B28-House of Friends",
  "B25-B28-Hudson Nonstop",
  "B25-B28-Wingstop",
  "B25-B28-Starbucks",
  "B25-B28-Vending Machine",
  "B29/B40-B41 Stinger Gateway-Cantina Laredo",
  "B29/B40-B41 Stinger Gateway-Caribou Coffee",
  "B29/B40-B41 Stinger Gateway-World News & Gifts",
  "B29/B40-B41 Stinger Gateway-Auntie Anne's",
  "B29/B40-B41 Stinger Gateway-McAlister's Deli",
  "B29/B40-B41 Stinger Gateway-Whataburger",
  "B30-B39 Stinger-World News & Gifts",
  "B30-B39 Stinger-The Rodeo Bar",
  "B30-B39 Stinger-Vending Machine",
  "B42-B49-Freeflight Sweets",
  "B42-B49-Sundance Travel Essentials",
  "B42-B49-Gameway",
  "B42-B49-InMotion",
  "B42-B49-Cousin's Bar-B-Q",
  "B42-B49-Cousin's Back Porch",
  "B42-B49-Vending Machine",
  "C2-C12-Starbucks",
  "C2-C12-Natalie's Candy Jar",
  "C2-C12-Newslink",
  "C2-C12-TGI Fridays",
  "C2-C12-ShopAll Electronics and Fans Trading Cards Vending",
  "C2-C12-3Sixty Duty Free and More",
  "C2-C12-Kylie Cosmetics Vending",
  "C2-C12-7-Eleven",
  "C2-C12-Whisk & Bowl",
  "C2-C12-M•A•C",
  "C2-C12-Spa Here",
  "C2-C12-Vending Machine",
  "C14-C20-Pappadeaux Seafood Bar",
  "C14-C20-Pappadeaux Seafood Kitchen",
  "C14-C20-Maggiano's",
  "C14-C20-Baskin-Robbins",
  "C14-C20-Dunkin'",
  "C14-C20-Univision",
  "C14-C20-Pappasito's Cantina",
  "C14-C20-Starbucks",
  "C14-C20-Vending Machine",
  "C21-C24-McDonald's",
  "C21-C24-Auntie Anne's",
  "C21-C24-Pizza Hut",
  "C21-C24-Banh Shop",
  "C21-C24-Pinkberry",
  "C21-C24-Chick-fil-A",
  "C21-C24-Vending Machine",
  "C26-C34 Reconstruction-Peach Cobbler Factory",
  "C26-C34 Reconstruction-Deep Ellum Bar + Kitchen",
  "C26-C34 Reconstruction-Air Essentials",
  "C35-C39-Chili's",
  "C35-C39-Fort Worth Magazine",
  "C35-C39-Boar's Head Deli",
  "C35-C39-Ampersand Coffee House",
  "C35-C39-Nowitzki",
  "C35-C39-Vending Machine",
  "D1-D4 Stinger-Trinity Groves Food Lockers",
  "D1-D4 Stinger-360 West Travel Essentials",
  "D1-D4 Stinger-Cake Bar",
  "D1-D4 Stinger-Trinity Groves Kitchen & Bar",
  "D1-D4 Stinger-Grab + Fly",
  "D5-D22 South Village-The Wall Street Journal",
  "D5-D22 South Village-Dude, Sweet Chocolate",
  "D5-D22 South Village-Banh Shop",
  "D5-D22 South Village-Applebee's",
  "D5-D22 South Village-Chick-fil-A",
  "D5-D22 South Village-Sugarphoria",
  "D5-D22 South Village-Lego",
  "D5-D22 South Village-Maceoo",
  "D5-D22 South Village-Flying Saucer Draught Emporium",
  "D5-D22 South Village-Lone Star Shade",
  "D5-D22 South Village-Vending Machine",
  "D21-D24 Center-Licorice Vending",
  "D21-D24 Center-iStore",
  "D21-D24 Center-Bar Louie",
  "D21-D24 Center-Be Relax",
  "D21-D24 Center-Hickory",
  "D21-D24 Center-3Sixty Duty Free and More",
  "D21-D24 Center-Chanel",
  "D21-D24 Center-Minute Suites",
  "D21-D24 Center-Chili's",
  "D21-D24 Center-Coach",
  "D21-D24 Center-Jo Malone",
  "D21-D24 Center-Longchamp",
  "D21-D24 Center-Montblanc",
  "D21-D24 Center-Diptyque",
  "D21-D24 Center-Travel+Leisure News",
  "D21-D24 Center-Vending Machine",
  "D23-D40 North Village-Dickey's Barbecue Pit",
  "D23-D40 North Village-Artisan Market",
  "D23-D40 North Village-Buffalo Wild Wings",
  "D23-D40 North Village-CRÚ Food & Wine Bar",
  "D23-D40 North Village-Kate Spade",
  "D23-D40 North Village-Michael Kors",
  "D23-D40 North Village-TX/MX",
  "D23-D40 North Village-InMotion",
  "D23-D40 North Village-3Sixty Duty Free and More",
  "D23-D40 North Village-Univision",
  "D23-D40 North Village-McDonald's",
  "D23-D40 North Village-Lavazza Coffee",
  "D23-D40 North Village-Coffee Bean & Tea Leaf + Bakery",
  "D23-D40 North Village-The Italian Kitchen by Wolfgang Puck",
  "D23-D40 North Village-Texas General Store",
  "D23-D40 North Village-World News & Gifts",
  "D23-D40 North Village-Pick-A-Bu Automated Store",
  "D23-D40 North Village-Vending Machine",
  "E2-E10-Drew Pearson's Sports 88",
  "E2-E10-Auntie Anne's",
  "E2-E10-Chick-fil-A",
  "E2-E10-ShopAll Fans Topps Trading Cards",
  "E2-E10-Dunkin'",
  "E2-E10-Hudson",
  "E2-E10-IHOP",
  "E2-E10-InMotion",
  "E2-E10-Vending Machine",
  "E11-E17-Love Shack",
  "E11-E17-Marshall Rousso",
  "E11-E17-7-Eleven",
  "E11-E17-Sonny Bryan's Smokehouse",
  "E11-E17-Einstein Bros. Bagels",
  "E11-E17-Premium Sound Vending",
  "E11-E17-InMotion",
  "E11-E17-Texas Marketplace",
  "E11-E17-Gameway",
  "E11-E17-TGI Fridays",
  "E11-E17-Vending Machine",
  "E18-E21-Brewed",
  "E18-E21-Jamba",
  "E18-E21-Jimmy John's",
  "E18-E21-Vending Machine",
  "E22-E30 Stinger-Starbucks",
  "E22-E30 Stinger-Freshëns Yogurt & Smoothies",
  "E22-E30 Stinger-Travel @ Ease",
  "E22-E30 Stinger-Dickey's Barbecue Pit",
  "E22-E30 Stinger-Whataburger",
  "E22-E30 Stinger-AV8 Bar & Cafe",
  "E22-E30 Stinger-Vending Machine",
  "E31-E34-2.0 Taco & Tequila Bar",
  "E31-E34-Auntie Anne's",
  "E31-E34-NBC-5 Travel Store",
  "E31-E34-Lick Candy Store",
  "E31-E34-Panda Express and Bar",
  "E31-E34-Wendy's",
  "E31-E34-Sound Balance",
  "E31-E34-Starbucks",
  "E31-E34-Lone Star Attitude",
  "E31-E34-Vending Machine",
  "E35-E38-Sundance Travel Essentials",
  "E35-E38-Uno Due Go",
  "E35-E38-Vending Machine",];

// "<zone>-<store>". The zone may carry a slashed gate ("B29/B40-B41") and a
// trailing area name ("D23-D40 North Village"); the store label is whatever
// follows, and may itself contain hyphens ("Baskin-Robbins").
const DFW_STORE_NAME_PATTERN =
  /^([A-E]\d+(?:\/[A-E]\d+)?-[A-E]\d+(?:\s+[^-]+?)?)-(.+)$/;

interface ParsedStoreName {
  zone: string;
  label: string;
}

function parseStoreName(name: string): ParsedStoreName | null {
  const match = name.match(DFW_STORE_NAME_PATTERN);
  if (!match) {
    return null;
  }
  return { zone: match[1], label: match[2] };
}

function zoneRank(zone: string): [string, number] {
  const match = zone.match(/^([A-E])(\d+)/);
  if (!match) {
    return ["Z", Number.MAX_SAFE_INTEGER];
  }
  return [match[1], parseInt(match[2], 10)];
}

function curatedKey(parsed: ParsedStoreName): string {
  return `${parsed.zone}::${parsed.label}`;
}

// Curated key -> position in the list above. Zones are contiguous there, so the
// flat index doubles as the within-zone rank once zone order has been settled.
const CURATED_RANKS = new Map<string, number>();
DFW_STORE_ORDER.forEach((name, index) => {
  const parsed = parseStoreName(name);
  if (!parsed) {
    return;
  }
  // "A35-A39-Southwest News" is listed twice — two distinct stores share a
  // name. First occurrence wins; the pair sorts adjacently either way.
  if (!CURATED_RANKS.has(curatedKey(parsed))) {
    CURATED_RANKS.set(curatedKey(parsed), index);
  }
});

function compareDfwStoreNames(a: string, b: string): number {
  const parsedA = parseStoreName(a);
  const parsedB = parseStoreName(b);

  // A name with no zone prefix can't be placed in the curated order. Group
  // those at the end instead of interleaving them through the zones.
  if (!parsedA || !parsedB) {
    if (!parsedA && !parsedB) {
      return compareGateNames(a, b);
    }
    return parsedA ? -1 : 1;
  }

  if (parsedA.zone !== parsedB.zone) {
    const [letterA, gateA] = zoneRank(parsedA.zone);
    const [letterB, gateB] = zoneRank(parsedB.zone);

    if (letterA !== letterB) {
      return letterA.localeCompare(letterB);
    }
    if (gateA !== gateB) {
      return gateA - gateB;
    }
    return parsedA.zone.localeCompare(parsedB.zone);
  }

  const rankA = CURATED_RANKS.get(curatedKey(parsedA)) ?? Infinity;
  const rankB = CURATED_RANKS.get(curatedKey(parsedB)) ?? Infinity;

  if (rankA !== rankB) {
    return rankA - rankB;
  }

  // Both unlisted in this zone (newly opened or renamed): alphabetical, after
  // every curated store in the same zone.
  return compareGateNames(parsedA.label, parsedB.label);
}

type StoreComparator = (a: string, b: string) => number;

// Airports with their own store order, keyed by IATA code. Add an entry here to
// give another airport a custom order; anything absent uses compareGateNames.
const STORE_COMPARATORS: Readonly<Record<string, StoreComparator>> = {
  DFW: compareDfwStoreNames,
};

/**
 * Comparator for a given airport's store names, for use with `Array.sort`.
 * Falls back to the generic alphanumeric sort when the airport has no custom
 * order — which is every airport but DFW today.
 */
export function getStoreComparator(codeIata: string): StoreComparator {
  return STORE_COMPARATORS[codeIata] ?? compareGateNames;
}
